import { useMutation, useQuery } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CREATE_TEMPLATE_GROUP,
  UPDATE_TEMPLATE_GROUP,
  DELETE_TEMPLATE_GROUP,
} from "@graphql/mutations/TemplateGroups";
import { GET_TEMPLATE_GROUPS } from "@graphql/queries/TemplateGroups";
import { useAuth } from "@hooks";
import { useState } from "react";

import DeleteTemplateGroupModal from "./DeleteTemplateGroupModal";
import TemplateGroupCard from "./TemplateGroupCard";
import TemplateGroupModal from "./TemplateGroupModal";

const TemplateGroupSection = ({
  templates,
  showCreateModal: externalCreateOpen = false,
  onCreateModalClose,
}) => {
  const { userUuid } = useAuth();
  const { data, loading } = useQuery(GET_TEMPLATE_GROUPS, {
    fetchPolicy: "cache-and-network",
  });

  const refetchGroups = { refetchQueries: ["GetTemplateGroups"] };

  const [createTemplateGroup, { loading: creating }] = useMutation(
    CREATE_TEMPLATE_GROUP,
    refetchGroups,
  );
  const [updateTemplateGroup, { loading: updating }] = useMutation(
    UPDATE_TEMPLATE_GROUP,
    refetchGroups,
  );
  const [deleteTemplateGroup, { loading: deleting }] = useMutation(
    DELETE_TEMPLATE_GROUP,
    refetchGroups,
  );

  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const isCreateModalOpen = internalCreateOpen || externalCreateOpen;
  const closeCreateModal = () => {
    setInternalCreateOpen(false);
    onCreateModalClose?.();
  };
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);

  const templateGroups = data?.templateGroups || [];

  const handleCreateSave = async ({
    name,
    templateUuids,
    sharingType,
  }) => {
    try {
      await createTemplateGroup({
        variables: { name, templateUuids, sharingType },
      });
      alert("success", `Template group "${name}" created.`);
      closeCreateModal();
    } catch (err) {
      console.error("Create template group error:", err);
      alert(
        "error",
        err?.message ||
          "Failed to create template group. Please try again.",
      );
    }
  };

  const handleEditSave = async ({
    name,
    templateUuids,
    sharingType,
  }) => {
    try {
      await updateTemplateGroup({
        variables: {
          groupUuid: editingGroup.uuid,
          name,
          templateUuids,
          sharingType,
        },
      });
      alert("success", `Template group "${name}" updated.`);
      setEditingGroup(null);
    } catch (err) {
      console.error("Update template group error:", err);
      alert(
        "error",
        err?.message ||
          "Failed to update template group. Please try again.",
      );
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTemplateGroup({
        variables: { groupUuid: deletingGroup.uuid },
      });
      alert(
        "success",
        `Template group "${deletingGroup.name}" deleted.`,
      );
      setDeletingGroup(null);
    } catch (err) {
      alert(
        "error",
        "Failed to delete template group. Please try again.",
      );
    }
  };

  const getEditingTemplates = (group) => {
    if (!group?.templates) return [];
    return [...group.templates]
      .sort((a, b) => a.position - b.position)
      .map((t) => t.autoTemplate)
      .filter(Boolean);
  };

  if (loading && templateGroups.length === 0) return null;

  return (
    <>
      <div data-testid="template-groups-section">
        <div className="mb-sm">
          <h3 className="text-xl font-semibold">Template Groups</h3>
          <p className="text-xs font-normal text-secondary">
            Pre-configured sets of templates for grouped notes.
          </p>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-md rounded-large">
          {templateGroups.map((group) => (
            <TemplateGroupCard
              key={group.uuid}
              group={group}
              isOwner={group.user?.uuid === userUuid}
              onEdit={setEditingGroup}
              onDelete={setDeletingGroup}
              dataTestId={`template-group-card-${group.uuid}`}
            />
          ))}
          <div className="relative flex">
            <button
              className="w-full h-full flex flex-col items-center justify-center flex-none border-2 border-dashed border-secondary rounded-standard p-md text-secondary hover:bg-secondary transition"
              onClick={() => setInternalCreateOpen(true)}
              data-testid="create-template-group-button"
            >
              <FontAwesomeIcon icon={faPlus} size="lg" />
            </button>
          </div>
        </div>
      </div>

      {/* Create modal */}
      <TemplateGroupModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onSave={handleCreateSave}
        saving={creating}
        availableTemplates={templates}
      />

      {/* Edit modal */}
      <TemplateGroupModal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        onSave={handleEditSave}
        saving={updating}
        initialName={editingGroup?.name || ""}
        initialTemplates={getEditingTemplates(editingGroup)}
        initialSharingType={editingGroup?.sharingType || "PRIVATE"}
        availableTemplates={templates}
        isEditing
      />

      {/* Delete confirmation */}
      <DeleteTemplateGroupModal
        isOpen={!!deletingGroup}
        onClose={() => setDeletingGroup(null)}
        onConfirm={handleDeleteConfirm}
        groupName={deletingGroup?.name || ""}
        deleting={deleting}
      />
    </>
  );
};

export default TemplateGroupSection;
